import { Link, router, usePage } from "@inertiajs/react";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { Button } from "@/components/ui/button";
import { LockedButton } from "@/components/billing/LockedButton";
import {
  ExternalLink,
  MapPin,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import type { Business, PageProps } from "@/types";

interface BusinessItem extends Business {
  google_maps_url: string | null;
}

interface Props {
  businesses: BusinessItem[];
  meta: { count: number; limit: number | null };
}

export default function BusinessesIndex({ businesses, meta }: Props) {
  const {
    current_user,
    current_organization,
    current_business,
    org_slug,
    flash,
  } = usePage<PageProps>().props;

  const atLimit = meta.limit != null && meta.count >= meta.limit;

  const handleDelete = (business: BusinessItem) => {
    if (confirm(`¿Estás seguro de eliminar "${business.name}"?`)) {
      router.delete(`/${org_slug}/businesses/${business.id}`);
    }
  };

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={org_slug!}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Sucursales
            </h1>
            <p className="text-muted-foreground">
              Administra las ubicaciones de tu negocio
              {meta.limit && (
                <span>
                  {" "}· {meta.count}/{meta.limit}
                </span>
              )}
            </p>
          </div>
          {atLimit ? (
            <LockedButton orgSlug={org_slug!} isLocked={true} limitKey="branches">
              Nueva sucursal
            </LockedButton>
          ) : (
            <Link href={`/${org_slug}/businesses/new`} className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto">
                <Plus className="mr-2 h-4 w-4" />
                Nueva sucursal
              </Button>
            </Link>
          )}
        </div>

        {flash?.notice && (
          <div className="rounded-lg bg-green-500/10 p-3 text-sm text-green-500">
            {flash.notice}
          </div>
        )}

        {flash?.alert && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            {flash.alert}
          </div>
        )}

        {businesses.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center">
            <p className="text-muted-foreground">
              No tienes sucursales registradas
            </p>
            <Link href={`/${org_slug}/businesses/new`} className="mt-4 inline-block">
              <Button variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Crear tu primera sucursal
              </Button>
            </Link>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card">
            <div className="divide-y divide-border">
              {businesses.map((business) => (
                <div
                  key={business.id}
                  className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <Link
                    href={`/${org_slug}/businesses/${business.id}`}
                    className="flex min-w-0 items-center gap-4 rounded-lg hover:bg-accent/50"
                  >
                    <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-muted flex items-center justify-center text-muted-foreground text-lg">
                      🏪
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-card-foreground truncate">
                        {business.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        {business.address ? (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="truncate">{business.address}</span>
                            {business.google_maps_url && (
                              <a
                                href={business.google_maps_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary/80 flex-shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/60 italic">Sin dirección</span>
                        )}
                        <span>{business.timezone}</span>
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 pl-14 sm:pl-0">
                    <Link href={`/${org_slug}/businesses/${business.id}/edit`}>
                      <Button variant="ghost" size="sm">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </Link>
                    {businesses.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(business)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
